import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Paper,
  Autocomplete,
  Avatar,
} from "@mui/material";
import { Grid, Stack } from "@mui/system";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectAllUsers,
  selectUserToken,
} from "../../../../../../features/userSlice";
import { ActionItemsEndPointLogic } from "../../../../../../logic/AssetManagement/ActionItems/actionItemsEndPointLogic";
import { useDispatch } from "react-redux";
import { setSelectedRows } from "../../../../../../features/actionItemsSlice";
import { useUpdateAcionItemsStatus } from "../../../../../../customHooks/useUpdateAcionItemsStatus";

const AssignPopup = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const [assigneeName, setAssigneeName] = useState<any>(null);
  const [assigneeId, setAssigneeId] = useState<any>(null);
  const { loading, updateActionItemsStatus } =
    useUpdateAcionItemsStatus("assigned"); // Custom hook for updating assignee
  const [comment, setComment] = useState<string>("");
  const { selectedRows } = useSelector((state: any) => state.actionItems);

  const userToken = useSelector(selectUserToken) as any;
  const allUsers = useSelector(selectAllUsers);

  const dispatch = useDispatch();

  const [selected, setSelected] = useState<any[]>(selectedRows);

  //-- Check if all rows are selected
  const isAllSelected = selected.length === selectedRows.length;

  //-- Handle individual row selection
  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row]
    );
  };

  //-- Handle "Select All" checkbox
  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRows]);
  };

  //-- Change Assigneee name
  const handleAssigneeChange = (value: any) => {
    setAssigneeId(value?.id || null);
    setAssigneeName(value || null);
  };

  //-- Get Users
  useEffect(() => {
    ActionItemsEndPointLogic.getAllUserList(userToken);
  }, [userToken]);

  //-- Close the Assign modal and clear the selected rows(unchecked checkboxes)
  const handleCloseAssignModal = () => {
    handleClose(); // Close modal if success
    dispatch(setSelectedRows([])); // Clear selected rows
  };

  //-- Request body for updating task assignee
  const assignBodyActionItem = {
    tasks: selected.map((task) => ({
      shared_uid: task.shared_uid,
      assignee: assigneeId,
      current_status:task.current_status,
      
    })),
  };

  //-- Clicking the Assign button to update the assignee
  const handleActions = async () => {
    if (!assigneeId || selected.length === 0) return;
    //-- this function for update status(MAKE API call for update status)
    updateActionItemsStatus(
      assignBodyActionItem,
      comment,
      handleCloseAssignModal
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (
          loading &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }
        handleCloseAssignModal();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Assign Action Items</DialogTitle>
      <DialogContent>
        <Typography gutterBottom sx={{ paddingBottom: "8px" }}>
          Selected Items
        </Typography>
        <Paper
          sx={{
            width: "100%",
            overflowY: "auto",
            height: "auto",
            maxHeight: "225px",
            scrollbarWidth: "thin",
          }}
        >
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ padding: "4px" }}>Item Name</TableCell>
                <TableCell sx={{ padding: "4px" }}>Target</TableCell>
                <TableCell sx={{ padding: "4px" }}>Assignee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRows?.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={selected.some((r) => r === row)}
                      onChange={() => handleSelectRow(row)}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row?.data["action_item"]}
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row?.data["target"]}
                  </TableCell>
                  <TableCell align="left" sx={{ padding: "4px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 22,
                          height: 22,
                          fontSize: 12,
                        }}
                      >
                        {row?.assignee_fullname.charAt(0).toUpperCase()}
                      </Avatar>
                      {row?.assignee_fullname}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Typography gutterBottom marginBlock={2}>
          Stakeholder
        </Typography>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Autocomplete
              value={assigneeName} // Ensure assignee is an object like { id: number, name: string }
              onChange={(_, newValue) => handleAssigneeChange(newValue)}
              options={allUsers} // Use the full user objects as options
              getOptionLabel={(option) =>
                option?.first_name + " " + option?.last_name
              } // Display name correctly
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assignee"
                  size="small"
                  placeholder="Choose Stakeholder from this list"
                  sx={{ input: { cursor: "pointer" } }} // show pointer on hover
                  onKeyDown={(e) => e.preventDefault()} // Blocks manual typing
                  InputLabelProps={{
                    shrink: true, // Keeps label above the input field
                  }}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  required
                  color="secondary"
                  helperText="These fields are mandatory"
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Reason"
              size="small"
              color="secondary"
              required={selected?.length > 1}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Why item is assigned to this specific user"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        <Button
          onClick={() => {
            handleClose();
            dispatch(setSelectedRows([]));
          }}
          color="secondary"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          data-tracker="118"
          variant="contained"
          color="secondary"
          loading={loading}
          loadingPosition="end"
          endIcon={loading && <SaveIcon />}
          disabled={
            selected.length === 0 ||
            !assigneeId ||
            (selected.length > 1 && (!comment || !assigneeId))
          }
          onClick={() => handleActions()}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            Assign
            <CheckIcon fontSize="small" />
          </Stack>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPopup;
